import { useCallback, useEffect, useState } from 'react'
import { errorMessage, isAbort } from '../pb'
import { listApproved, listMine } from '../recipes'
import type { Recipe } from '../recipes'
import { loadBoard } from '../sisters'
import type { BoardEntry } from '../sisters'

/**
 * The board and the table, loaded once for the whole signed-in site.
 *
 * Every tab reads the same two lists, and the search box reads both, so they live above the
 * tabs rather than being fetched again each time she moves.
 */
export interface MembersData {
  board: BoardEntry[] | null
  recipes: Recipe[] | null
  mine: Recipe[] | null
  error: string | null
  refreshBoard: () => Promise<void>
  refreshRecipes: () => Promise<void>
}

export function useMembersData(myUserId: string): MembersData {
  const [board, setBoard] = useState<BoardEntry[] | null>(null)
  const [recipes, setRecipes] = useState<Recipe[] | null>(null)
  const [mine, setMine] = useState<Recipe[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refreshBoard = useCallback(async () => {
    try {
      setBoard(await loadBoard(myUserId))
    } catch (err) {
      if (isAbort(err)) return
      setError(errorMessage(err, 'Could not load the board'))
    }
  }, [myUserId])

  const refreshRecipes = useCallback(async () => {
    try {
      setRecipes(await listApproved())
    } catch (err) {
      if (isAbort(err)) return
      setError(errorMessage(err, 'Could not load the recipes'))
    }
    try {
      setMine(await listMine(myUserId))
    } catch {
      /* the shared list is the page; her own is an extra and not worth an error */
    }
  }, [myUserId])

  useEffect(() => {
    void refreshBoard()
    void refreshRecipes()
  }, [refreshBoard, refreshRecipes])

  return { board, recipes, mine, error, refreshBoard, refreshRecipes }
}
